import React from "react";

/**
 * Parse teleprompter markup into colored segments.
 *
 * Markers:
 *  - [texto]      → white
 *  - {texto}      → orange
 *  - (texto)      → blue
 *  - ((texto))    → red + blinking
 *
 * The brackets themselves are hidden in the output; only the inner text
 * is rendered with the matching style. Supports nesting of (( )) only
 * (double-paren takes precedence over single paren).
 */
export function renderPrompterText(text, { baseColorClass = "text-white" } = {}) {
  if (!text) return null;

  // Tokenize: find all markers in order and build an array of
  // { type: 'plain'|'white'|'orange'|'blue'|'red', content }
  // We scan with a regex that captures the longest alternatives first.
  const RE = /(\(\([^()]*\)\))|(\[[^\[\]]*\])|(\{[^{}]*\})|(\([^()]*\))/g;
  const segments = [];
  let last = 0;
  let m;
  while ((m = RE.exec(text)) !== null) {
    if (m.index > last) {
      segments.push({ type: "plain", content: text.slice(last, m.index) });
    }
    const raw = m[0];
    if (raw.startsWith("((") && raw.endsWith("))")) {
      segments.push({ type: "red", content: raw.slice(2, -2) });
    } else if (raw.startsWith("[") && raw.endsWith("]")) {
      segments.push({ type: "white", content: raw.slice(1, -1) });
    } else if (raw.startsWith("{") && raw.endsWith("}")) {
      segments.push({ type: "orange", content: raw.slice(1, -1) });
    } else if (raw.startsWith("(") && raw.endsWith(")")) {
      segments.push({ type: "blue", content: raw.slice(1, -1) });
    }
    last = m.index + raw.length;
  }
  if (last < text.length) {
    segments.push({ type: "plain", content: text.slice(last) });
  }

  const classFor = (t) => {
    switch (t) {
      case "white":
        return "text-white";
      case "orange":
        return "text-orange-400";
      case "blue":
        return "text-sky-400";
      case "red":
        return "text-red-500 animate-pulse";
      default:
        return baseColorClass;
    }
  };

  return segments.map((seg, i) => (
    <span key={i} className={classFor(seg.type)}>
      {seg.content}
    </span>
  ));
}
